import type { CallbackDataParams } from 'echarts/types/dist/shared.js'
import { toLower } from 'lodash-es'

import { IChartSeries, ItemValue, LineSegmentGenericData } from '$/types'
import { countDigits } from 'common/utils/numbers'

import {
  formatWithPrecision,
  ONE_DEC_ATTR_NAMES,
  THREE_DEC_ATTR_NAMES,
  ZERO_DEC_ATTR_NAMES
} from 'features/RiskManagement/Components/Graphs/Implementations/attributeFormatters'
import { RiskUnitType } from 'features/RiskManagement/Components/LocalFilter'

export interface ICategoryChartParams {
  categories: string[]
  series: IChartSeries[]
  unitSymbol?: string
}

// NOTE: Partially extracted from ECharts Type declaration
export interface ITooltipCallbackDataParam {
  name: string
  dataIndex: number
  seriesIndex: number
  value: string | number
  color?: string
  borderColor?: string
  percent?: number
}

/**
 * Function that determines a chart label given the risk unit selected. Can be refactored to account for
 * more risk units in the future.
 * @param riskUnit Risk unit selected
 * @param totalRiskLabel Label to show when the risk unit is Total Risk. Defaults to 'Risk (RU/yr)'
 * @param riskDensityLabel Label to show when the risk unit is Risk Density. Defaults to 'Risk (RU/mi/yr)'
 * @returns String that represents what label to show
 */
export const getChartLabelBasedOnRiskUnit = (
  riskUnit: RiskUnitType,
  totalRiskLabel = 'Risk (RU/yr)',
  riskDensityLabel = 'Risk (RU/mi/yr)'
): string => {
  switch (riskUnit) {
    case RiskUnitType.TotalRisk:
      return totalRiskLabel
    case RiskUnitType.RiskDensity:
      return riskDensityLabel
  }
}

// extend as needed based on the above functions (like Intl options for commas)
interface IFormatWithCommasOrExponentialOptions {
  decimals?: number
  isMeasure?: boolean
  isGraph?: boolean
  isDate?: boolean
  forceNormal?: boolean
  options?: Intl.NumberFormatOptions
  fallbackFormatter?: (value: number | string) => string
}

interface IChartParamsData {
  value: (number | string | boolean)[]
  fromMeasure: number
  toMeasure: number
  fullDate?: string
  visualMap: boolean
}

// standardize number formatting to single digit decimal (for charts)
export const formatExponentialValue = (value: number | string, decimals = 1) =>
  !value ? '0' : Number(value).toExponential(decimals).toUpperCase()

// standardize formatting numbers with commas
export const formatNumberWithCommas = (
  value: number | string,
  options: IFormatWithCommasOrExponentialOptions['options']
) =>
  Number(value).toLocaleString('en-US', {
    maximumFractionDigits: 2,
    ...options
  })

const truncateToFractionDigits = (value: number, fractionDigits: number) => {
  if (!Number.isFinite(value)) {
    return value
  }

  const factor = 10 ** Math.max(0, fractionDigits)

  return Math.trunc(value * factor) / factor
}

// donut chart legends and tooltips are hard set to 3 demicals
export const chopAndFormatNumberWithCommas = (
  value: number,
  options: Intl.NumberFormatOptions = {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3
  }
) => {
  const fractionDigits =
    options.maximumFractionDigits ?? options.minimumFractionDigits ?? 3

  const truncatedValue = truncateToFractionDigits(value, fractionDigits)

  return formatNumberWithCommas(truncatedValue, options)
}

// standardize selection logic here. reference above functions for arguments
export const formatWithCommasOrExponential = (
  value: string | number,
  options: IFormatWithCommasOrExponentialOptions = {}
) => {
  if (value === null) {
    return options.fallbackFormatter?.(value) ?? 'NULL'
  }

  if (typeof value === 'boolean') {
    return (options.fallbackFormatter?.(value) ?? value) ? 'True' : 'False'
  }

  const _number = Number(value)

  if (Number.isNaN(_number) || options.isDate) {
    return options.fallbackFormatter?.(value) ?? String(value)
  }

  // if this option is set, the return value could be rounded up to over
  // 1_000_000 which shold be formatted as exponential value
  const { maximumSignificantDigits } = options.options || {}
  const maxSigFigUndefined = typeof maximumSignificantDigits === 'undefined'

  const digits = countDigits(_number)

  const splitNumbers = _number.toString().split('.')
  // respect modal-specific override: if forceNormal is true, skip exponential
  // formatting for fractional length and scientific string branches

  if (
    splitNumbers.length === 2 &&
    splitNumbers[1].length >= 4 &&
    !options.isMeasure
  ) {
    // If the caller requested normal formatting, return a comma-formatted
    // numeric string instead of exponential. Otherwise fall back to
    // exponential formatting for long fractional parts.
    if (options.forceNormal) {
      return formatNumberWithCommas(_number, options.options)
    }

    return formatExponentialValue(_number, options.decimals)
  } else if (splitNumbers[0].includes('e')) {
    // If the caller requested normal formatting, just return a comma-formatted
    // numeric string instead of re-using the scientific string
    if (options.forceNormal) {
      return formatNumberWithCommas(_number, options.options)
    }

    // Handles the case where echarts automatically converts to sci notation when 7+ decimals
    const splitString = splitNumbers[0].split('e')

    if (!splitString[0].includes('.')) {
      splitString[0] += '.0'
    }

    return splitString[0] + 'E' + splitString[1]
  }

  // do not bother with pennies (since sigfigs are adjusted for risk units)
  if (!maxSigFigUndefined && digits < 2) {
    return formatNumberWithCommas(_number, {
      maximumFractionDigits: 2
    })
  }

  if (options.isMeasure) {
    if (options.isGraph && value == 0) {
      return '0'
    }

    return formatNumberWithCommas(_number, {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    })
  }

  // If zero, show normally with configured fraction digits
  if (_number === 0) {
    return formatNumberWithCommas(_number, {
      ...options.options,
      maximumFractionDigits: 3
    })
  }

  // comma-formatted numeric string for readability in special UIs. Otherwise
  // preserve the original behavior (exponential for non-zero values).
  if (options.forceNormal) {
    return formatNumberWithCommas(_number, {
      ...options.options,
      maximumFractionDigits: 3
    })
  }

  return _number !== 0
    ? formatExponentialValue(_number, options.decimals)
    : formatNumberWithCommas(_number, {
        ...options.options,
        maximumFractionDigits: 3
      })
}

export const makeRUoptions = (
  unit: string,
  options?: IFormatWithCommasOrExponentialOptions
) => {
  const _lUnit = unit.toLowerCase()

  if (_lUnit.includes('ru') || _lUnit.includes('risk')) {
    return {
      options: {
        maximumFractionDigits: 0,
        maximumSignificantDigits: 2
      }
    }
  } else if (_lUnit.includes('significance')) {
    return {
      isMeasure: true
    }
  }

  return options
}

export const formatLabelOnNewLine = (value: number | string) =>
  String(value).split(' ').join('\n')

export const selectDataLabel = (data: ItemValue) => data.label ?? data.key

export const lrChartTooltipFormatter = (
  params: CallbackDataParams[],
  attributeName?: string,
  dataView?: boolean,
  currentLineName?: string,
  isDate?: boolean
) =>
  params.map((param, index: number) => {
    const parent = document.createElement('div')
    const toRow = document.createElement('div')
    const fromRow = document.createElement('div')
    const wrapper = document.createElement('div')
    const marker = document.createElement('span')
    const text = document.createElement('span')
    const prev = document.createElement('span')
    const current = document.createElement('span')
    const textValue = document.createElement('span')
    const title = document.createElement('span')

    // first element doesn't get margin
    if (index) {
      parent.style.marginTop = '6px'
    }

    // apply to/from measure only to the first row
    if (!index) {
      title.style.fontWeight = 'bold'
      title.textContent = `${param.seriesName}`
      const { fromMeasure, toMeasure } = param.data as IChartParamsData

      toRow.style.display = 'flex'
      toRow.style.justifyContent = 'space-between'
      fromRow.style.display = 'flex'
      fromRow.style.justifyContent = 'space-between'

      const fromSpan = document.createElement('span')
      const fromValue = document.createElement('span')
      const toSpan = document.createElement('span')
      const toValue = document.createElement('span')

      fromSpan.style.paddingRight = '16px'
      toSpan.style.paddingRight = '16px'

      fromSpan.textContent = 'From Measure: '

      fromValue.textContent = formatWithCommasOrExponential(fromMeasure, {
        isMeasure: true
      })
      toSpan.textContent = 'To Measure: '

      toValue.textContent = formatWithCommasOrExponential(toMeasure, {
        isMeasure: true
      })

      toRow.appendChild(toSpan)
      toRow.appendChild(toValue)

      fromRow.appendChild(fromSpan)
      fromRow.appendChild(fromValue)
    }

    wrapper.style.marginTop = '6px'
    wrapper.style.display = 'flex'
    wrapper.style.alignItems = 'center'

    marker.style.width = '12px'
    marker.style.height = '12px'
    marker.style.backgroundColor = param.color as string

    const { value } = param.data as IChartParamsData
    let textValueContent = value[1] as unknown as string | number | null

    // nulls that came from the api (under a valid segment) should be displayed as null
    if (textValueContent === null && textValueContent !== undefined) {
      textValueContent = 'NULL'
    }

    const trimmedName = attributeName?.trim() ?? ''
    const isDateBasedAttribute =
      toLower(trimmedName || param.seriesName).includes('year') || isDate

    if (dataView) {
      prev.style.paddingLeft = '6px'
      prev.style.paddingRight = '16px'

      if (param.seriesIndex === 0) {
        // if in Baseline, route name on which the user clicked should be displayed
        if (params.length === 1) {
          prev.textContent = currentLineName ?? 'N/A'
        } else {
          prev.textContent = 'Previous'
        }
      } else {
        prev.textContent = 'Current'
      }

      current.style.paddingLeft = '6px'
      current.style.paddingRight = '16px'

      if (textValueContent === 'NULL') {
        textValue.textContent = textValueContent
      } else if (THREE_DEC_ATTR_NAMES.includes(trimmedName)) {
        textValue.textContent = formatWithPrecision(
          textValueContent,
          3,
          'Finite'
        )
      } else if (ONE_DEC_ATTR_NAMES.includes(trimmedName)) {
        textValue.textContent = formatWithPrecision(
          textValueContent,
          1,
          'Finite'
        )
      } else if (ZERO_DEC_ATTR_NAMES.includes(trimmedName)) {
        textValue.textContent = formatWithPrecision(
          textValueContent,
          0,
          'Finite'
        )
      } else if (isDateBasedAttribute) {
        if ((param?.data as IChartParamsData)?.fullDate) {
          textValue.textContent =
            (param.data as IChartParamsData).fullDate || 'NULL'
        } else {
          textValue.textContent = String(textValueContent)
        }
      } else {
        textValue.textContent = formatWithCommasOrExponential(
          textValueContent,
          {
            fallbackFormatter: (value) => {
              const str = String(value)

              return str.substring(str.indexOf(',') + 1)
            }
          }
        )
      }
    } else {
      text.style.paddingLeft = '6px'
      text.style.paddingRight = '6px'

      if (isDateBasedAttribute) {
        if ((param?.data as IChartParamsData)?.fullDate) {
          textValue.textContent =
            (param.data as IChartParamsData).fullDate || 'NULL'
        } else {
          textValue.textContent = String(textValueContent)
        }
      } else if (attributeName) {
        const trimmed = attributeName.trim()

        if (THREE_DEC_ATTR_NAMES.includes(trimmed)) {
          textValue.textContent = formatWithPrecision(
            textValueContent,
            3,
            'Finite'
          )
        } else if (ONE_DEC_ATTR_NAMES.includes(trimmed)) {
          textValue.textContent = formatWithPrecision(
            textValueContent,
            1,
            'Finite'
          )
        } else if (ZERO_DEC_ATTR_NAMES.includes(trimmed)) {
          textValue.textContent = formatWithPrecision(
            textValueContent,
            0,
            'Finite'
          )
        } else {
          // fallback — keeps your original commas/exponential logic
          textValue.textContent = formatWithCommasOrExponential(
            textValueContent,
            {
              /* same options / fallbackFormatter you already had */
            }
          )
        }
      }
    }

    if (textValue.textContent === '') {
      textValue.textContent = 'NULL'
    }

    textValue.style.marginLeft = 'auto'

    // On LR Charts any gaps in the data (segments missing because local filters) should be listed as N/A while actual
    if (value[1] === undefined && value[1] !== null) {
      value[1] = 'N/A'
    }

    // Due to historical data, the dataview tooltip needs to be rendered differnetly than wis
    if (dataView) {
      parent.appendChild(title)
      parent.appendChild(fromRow)
      parent.appendChild(toRow)
      parent.appendChild(wrapper)
      wrapper.appendChild(marker)
      wrapper.appendChild(prev)
      wrapper.appendChild(textValue)
    } else {
      parent.appendChild(fromRow)
      parent.appendChild(toRow)
      parent.appendChild(wrapper)
      wrapper.appendChild(marker)
      wrapper.appendChild(text)
      wrapper.appendChild(textValue)
    }

    return parent
  })

export const processDateYAxisOptions = (
  yAxisOptions: LineSegmentGenericData[]
) => {
  yAxisOptions = yAxisOptions
    .filter(
      (option) => option !== null && option !== '' && option !== undefined
    )
    .map((option) =>
      typeof option === 'string' ? option.split('-')[0] : option
    )
  yAxisOptions = Array.from(new Set(yAxisOptions))

  if (yAxisOptions.length < 2) {
    // null must have its own category if all dates fall within the same year
    yAxisOptions.push(null)
  } else {
    const years = yAxisOptions.map((option) =>
      typeof option === 'string' ? Number.parseInt(option, 10) : 0
    )

    yAxisOptions = yAxisOptions
      .map((option, idx) => ({ option, year: years[idx] }))
      .sort((a, b) => a.year - b.year)
      .map(({ option }) => option)
  }

  return yAxisOptions
}
